import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  LinearProgress,
} from '@mui/material';
import type { DeploymentStatus, Node } from '../types';
import { DeploymentStage, NodeRole } from '../types';

interface ProjectProgressProps {
  status: DeploymentStatus;
  nodes: Node[];
}

const DEPLOYMENT_STEPS = [
  { stage: DeploymentStage.CONFIGURATION, label: 'Configuration' },
  { stage: DeploymentStage.PROVISIONING, label: 'Provisioning' },
  { stage: DeploymentStage.SETUP_CLUSTER, label: 'Setup Cluster' },
  { stage: DeploymentStage.SETUP_APPLICATIONS, label: 'Setup Applications' },
  { stage: DeploymentStage.DONE, label: 'Done' },
];

export const ProjectProgress: React.FC<ProjectProgressProps> = ({ status, nodes }) => {
  const currentStepIndex = DEPLOYMENT_STEPS.findIndex((step) => step.stage === status.stage);
  const controlNodes = nodes.filter(n => n.role === NodeRole.CONTROL_NODE && !n.deletedAt);
  const workerNodes = nodes.filter(n => n.role === NodeRole.WORKER_NODE && !n.deletedAt);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Deployment in Progress
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        {status.stage === DeploymentStage.FAILED ? (
          <Alert severity="error">
            Deployment failed. Please check the logs or contact support.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Your project is being deployed. This may take several minutes.
            </Alert>
            <LinearProgress sx={{ mb: 3 }} />
            <Stepper activeStep={currentStepIndex} alternativeLabel>
              {DEPLOYMENT_STEPS.map((step) => (
                <Step key={step.stage}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </>
        )}
      </Paper>

      {(controlNodes.length > 0 || workerNodes.length > 0) && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Provisioned Hosts
          </Typography>

          {controlNodes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Control Nodes
              </Typography>
              {controlNodes.map((node) => (
                <Typography key={node.id} variant="body2" color="text.secondary">
                  {node.ip ? `${node.ip} (user: ${node.user || 'N/A'})` : 'Provisioning...'}
                </Typography>
              ))}
            </Box>
          )}

          {workerNodes.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Worker Nodes
              </Typography>
              {workerNodes.map((node) => (
                <Typography key={node.id} variant="body2" color="text.secondary">
                  {node.ip ? `${node.ip} (user: ${node.user || 'N/A'})` : 'Provisioning...'}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};
